<template>
  <div class="__CostForecast">
    <!-- Monthly Section -->
    <CSection margin-bottom="4rem">
      <CTitle large margin-bottom>
        {{ $t('Cost trends and forecasts') }}
      </CTitle>

      <ForecastChart
        ref="forecast-monthly-chart"
        name="cost-forecast-monthly-chart"
        :data="chartMonthlyData"
        :series="monthlySeries"
        :dateRange="budgetTerm"
        granularity="month"
        :currency="currency"
      >
        <template v-slot:head-content>
          <div class="__CostForecast-Summary">
            <Row gutter="md">
              <Col col="6" md="3">
                <div class="__CostForecast-SummaryItem">
                  <div class="__CostForecast-SummaryItemLabel">
                    {{ $t('Cost up to today') }}
                    <HelpPopover placement="bottom">{{
                      $t(
                        'If no budget has been set then this value will show the year-to-date cost. Otherwise, it will show the cost accrued for the budget period.'
                      )
                    }}</HelpPopover>
                  </div>
                  <div
                    class="__CostForecast-SummaryItemValue bold"
                    :class="{ center: showBudgetBarMonthly }"
                  >
                    <CCurrency :value="summaryMonthly.total" type="usd" />
                  </div>
                </div>
              </Col>

              <Col col="6" :md="showBudgetBarMonthly ? 3 : 6">
                <Row>
                  <Col :col="showBudgetBarMonthly ? 12 : 6">
                    <div class="__CostForecast-SummaryItem">
                      <div class="__CostForecast-SummaryItemLabel">
                        {{ $t('Forecast of total cost') }}
                        <HelpPopover placement="bottom">{{
                          $t(
                            'If no budget has been set then this value will show the expected cost from the beginning of the year until the end of forecasting or the end of the year, whichever is shorted. Otherwise, it will show the forecasted total for the budget period.'
                          )
                        }}</HelpPopover>
                      </div>
                      <div
                        class="__CostForecast-SummaryItemValue"
                        :class="{ small: showBudgetBarMonthly }"
                      >
                        <CCurrency :value="summaryMonthly.forecast" type="usd" />
                      </div>
                    </div>
                  </Col>

                  <Col :col="showBudgetBarMonthly ? 12 : 6">
                    <div class="__CostForecast-SummaryItem">
                      <div class="__CostForecast-SummaryItemLabel">
                        {{ $t('Forecast of remaining period') }}
                        <HelpPopover placement="bottom">{{
                          $t(
                            'This value shows the forecasted cost for the remainder of the budget period, or the forecast period if no budget is set.'
                          )
                        }}</HelpPopover>
                      </div>
                      <div
                        class="__CostForecast-SummaryItemValue"
                        :class="{ small: showBudgetBarMonthly }"
                      >
                        <CCurrency :value="summaryMonthly.remaining" type="usd" />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col col="12" md="6" v-show="showBudgetBarMonthly">
                <div class="__CostForecast-SummaryItem">
                  <div class="__CostForecast-SummaryItemLabel">
                    {{ $t('Budget progress') }}
                  </div>

                  <!-- Monthly budget progress bar -->
                  <ProgressBarChart
                    ref="cost-forecast-monthly-budget-progress"
                    name="forecast-monthly-budget-progress"
                    :data="progressMonthly"
                    :series="progressBarSeries"
                  >
                    <template v-slot:head-content>
                      <div class="__CostForecast-ProgressBar">
                        <span class="__CostForecast-ProgressBarHeadLabel">
                          {{ $t('Current cost:') }}
                        </span>
                        <CCurrency :value="summaryMonthly.total" type="usd" />

                        <span class="__CostForecast-ProgressBarHeadSeparate">/</span>

                        <span class="__CostForecast-ProgressBarHeadLabel">
                          {{ $t('Remaining:') }}
                        </span>
                        <CCurrency :value="summaryMonthly.budgetRemaining" type="usd" />
                      </div>
                    </template>

                    <template v-slot:foot-content>
                      <div class="__CostForecast-ProgressBarFootContent">
                        <span class="mr-xs">{{ $t('Budget') }}:</span>
                        <CCurrency :value="summaryMonthly.budgetTotal" type="usd" />
                      </div>
                    </template>
                  </ProgressBarChart>
                </div>
              </Col>
            </Row>
          </div>
        </template>

        <template v-slot:foot-content>
          <div class="text-xs secondary-color text-right">
            {{ $t('Last updated') }}:
            <CDate :value="chartMonthlyLatestUpdateTime" format="YtoSec" />
          </div>
        </template>
      </ForecastChart>
    </CSection>

    <!-- Month to date Section -->
    <CSection>
      <CTitle large margin-bottom>
        {{ $t('Cost trends and forecasts for this month') }}
      </CTitle>

      <div class="__CostForecast-Summary">
        <Row gutter="md">
          <Col col="6" md="3">
            <div class="__CostForecast-SummaryItem">
              <div class="__CostForecast-SummaryItemLabel">
                {{ $t('Cost up to today') }}
              </div>
              <div
                class="__CostForecast-SummaryItemValue bold"
                :class="{ center: showBudgetBarMonthToDate }"
              >
                <CCurrency :value="summaryDaily.total" type="usd" />
              </div>
            </div>
          </Col>

          <Col col="6" :md="showBudgetBarMonthToDate ? 3 : 6">
            <Row>
              <Col :col="showBudgetBarMonthToDate ? 12 : 6">
                <div class="__CostForecast-SummaryItem">
                  <div class="__CostForecast-SummaryItemLabel">
                    {{ $t('Forecast of total cost') }}
                  </div>
                  <div
                    class="__CostForecast-SummaryItemValue"
                    :class="{ small: showBudgetBarMonthToDate }"
                  >
                    <CCurrency :value="summaryDaily.forecast" type="usd" />
                  </div>
                </div>
              </Col>

              <Col :col="showBudgetBarMonthToDate ? 12 : 6">
                <div class="__CostForecast-SummaryItem">
                  <div class="__CostForecast-SummaryItemLabel">
                    {{ $t('Forecast of remaining period') }}
                  </div>
                  <div
                    class="__CostForecast-SummaryItemValue"
                    :class="{ small: showBudgetBarMonthToDate }"
                  >
                    <CCurrency :value="summaryDaily.remaining" type="usd" />
                  </div>
                </div>
              </Col>
            </Row>
          </Col>

          <Col col="12" md="6" v-show="showBudgetBarMonthToDate">
            <div class="__CostForecast-SummaryItem">
              <div class="__CostForecast-SummaryItemLabel">
                {{ $t('Budget progress') }}
              </div>

              <!-- Month to date budget progress bar -->
              <ProgressBarChart
                ref="cost-forecast-mtd-budget-progress"
                name="forecast-mtd-budget-progress"
                :data="progressMonthToDate"
                :series="progressBarSeries"
              >
                <template v-slot:head-content>
                  <div class="__CostForecast-ProgressBar">
                    <span class="__CostForecast-ProgressBarHeadLabel">
                      {{ $t('Current cost:') }}
                    </span>
                    <CCurrency :value="summaryDaily.total" type="usd" />

                    <span class="__CostForecast-ProgressBarHeadSeparate">/</span>

                    <span class="__CostForecast-ProgressBarHeadLabel">
                      {{ $t('Remaining:') }}
                    </span>
                    <CCurrency :value="summaryDaily.budgetRemaining" type="usd" />
                  </div>
                </template>

                <template v-slot:foot-content>
                  <div class="__CostForecast-ProgressBarFootContent">
                    <span class="mr-xs">{{ $t('Budget') }}:</span>
                    <CCurrency :value="summaryDaily.budgetTotal" type="usd" />
                  </div>
                </template>
              </ProgressBarChart>
            </div>
          </Col>
        </Row>
      </div>

      <Row gutter="md">
        <Col col="12" lg="6">
          <ForecastChart
            ref="forecast-mtd-chart"
            name="cost-forecast-mtd-chart"
            :data="chartMonthToDateData"
            :series="monthToDateSeries"
            granularity="day"
            :value-range="chartMonthToDateBudget"
            :currency="currency"
          >
            <template v-slot:foot-content>
              <div class="text-xs secondary-color text-right">
                {{ $t('Last updated') }}:
                <CDate :value="chartMonthToDateLatestUpdateTime" format="YtoSec" />
              </div>
            </template>
          </ForecastChart>
        </Col>

        <Col col="12" lg="6">
          <ForecastChartBar
            ref="forecast-mom-chart"
            name="cost-forecast-mom-chart"
            :data="chartMonthOnMonthData"
            :series="monthOnMonthSeries"
            :currency="currency"
          >
            <template v-slot:foot-content>
              <div class="text-xs secondary-color text-right">
                {{ $t('Last updated') }}:
                <CDate :value="chartMonthOnMonthLatestUpdateTime" format="YtoSec" />
              </div>
            </template>
          </ForecastChartBar>
        </Col>
      </Row>
    </CSection>
  </div>
</template>

<script>
import Vue from 'vue';

import Row from '@design-system/components/Row.vue';
import Col from '@design-system/components/Col.vue';

import CPanel from '@/components/uikit/CPanel';
import CLabelValue from '@/components/uikit/CLabelValue';
import CCurrency from '@/components/uikit/CCurrency';
import CLoading from '@/components/uikit/CLoading';
import CDate from '@/components/uikit/CDate';

// Charts
import ForecastChart from '@/components/CustomCharts/ForecastChart';
import ForecastChartBar from '@/components/CustomCharts/ForecastChartBar';
import ProgressBarChart from '@/components/CustomCharts/ProgressBarChart';

import { mapActions, mapGetters, mapMutations } from 'vuex';
import CTitle from '@/components/uikit/CTitle';
import CButton from '@/components/uikit/CButton';
import CSection from '@/components/uikit/CSection';
import * as am4core from '@amcharts/amcharts4/core';
import chartColors from '@/components/CustomCharts/colors';
import HelpPopover from '@/components/uikit/HelpPopover';

const messages = {
  en: {
    'If no budget has been set then this value will show the year-to-date cost. Otherwise, it will show the cost accrued for the budget period.':
      'If no budget has been set then this value will show the year-to-date cost. Otherwise, it will show the cost accrued for the budget period.',
    'If no budget has been set then this value will show the expected cost from the beginning of the year until the end of forecasting or the end of the year, whichever is shorted. Otherwise, it will show the forecasted total for the budget period.':
      'If no budget has been set then this value will show the expected cost from the beginning of the year until the end of forecasting or the end of the year, whichever is shorted. Otherwise, it will show the forecasted total for the budget period.',
    'This value shows the forecasted cost for the remainder of the budget period, or the forecast period if no budget is set.':
      'This value shows the forecasted cost for the remainder of the budget period, or the forecast period if no budget is set.',
    'Cost trends and forecasts': 'Cost Trends & Forecasts',
    'Cost up to today': 'Accrued Cost',
    'Forecast of total cost': 'Forecasted Cost',
    'Forecast of remaining period': 'Remaining Cost',
    'Budget progress': 'Budget progress',
    'Current cost:': 'Current cost:',
    'Remaining:': 'Remaining:',
    Budget: 'Budget',
    'Last updated': 'Last updated',
    Cost: 'Cost',
    Forecast: 'Forecast',
    'Upper bound': 'Upper Bound',
    'Lower bound': 'Lower Bound',
    'Previous month': 'Previous Month',
    'Cost trends and forecasts for this month': `This Month's Cost Trends & Forecasts`,
  },
  ja: {
    'If no budget has been set then this value will show the year-to-date cost. Otherwise, it will show the cost accrued for the budget period.':
      '予算が設定されていない場合、この値は年初来のコストを示します。 それ以外の場合は、予算期間中に発生したコストが表示されます。',
    'If no budget has been set then this value will show the expected cost from the beginning of the year until the end of forecasting or the end of the year, whichever is shorted. Otherwise, it will show the forecasted total for the budget period.':
      '予算が設定されていない場合、この値は、年の初めから予測の終わりまたは年の終わりのいずれか短い方までの予想コストを示します。 それ以外の場合は、予算期間の予測合計が表示されます。',
    'This value shows the forecasted cost for the remainder of the budget period, or the forecast period if no budget is set.':
      'この値は、残りの予算期間または予算が設定されていない場合は予測期間の予測コストを示します。',
    'Cost trends and forecasts': 'コスト推移と予測',
    'Cost up to today': '今日までの利用額',
    'Forecast of total cost': '利用総額の予測',
    'Forecast of remaining period': '残り期間の予測',
    'Budget progress': '予算の進捗',
    'Current cost:': '利用額:',
    'Remaining:': '残り:',
    Budget: '予算',
    'Last updated': '更新日時',
    Cost: 'コスト',
    Forecast: '予測値',
    'Upper bound': '上限値',
    'Lower bound': '下限値',
    'Previous month': '先月のコスト',
    'Cost trends and forecasts for this month': '今月のコスト推移予測',
  },
};

export default Vue.extend({
  name: 'CostForecast',
  i18n: { messages },
  components: {
    CPanel,
    CSection,
    CButton,
    CTitle,
    Row,
    Col,
    ForecastChart,
    ForecastChartBar,
    ProgressBarChart,
    CLabelValue,
    CCurrency,
    CLoading,
    CDate,
    HelpPopover,
  },
  props: {
    id: {
      type: String,
    },
    type: {
      type: String,
      required: true,
      validator: function(value) {
        return ['account', 'acc_group', 'cross_tag'].indexOf(value) !== -1;
      },
    },
  },
  data() {
    return {
      currency: 'usd',

      monthToDateSeries: [
        {
          id: 'cost',
          name: this.$t('Cost'),
          color: 'blue',
          fill: true,
        },
        {
          id: 'forecast',
          name: this.$t('Forecast'),
          color: 'indigo',
          strokeType: 'dashed',
        },
        {
          id: 'upperBound',
          name: this.$t('Upper bound'),
          color: 'red',
          strokeType: 'dashed',
          strokeOpacity: 0.5,
        },
        {
          id: 'lowerBound',
          name: this.$t('Lower bound'),
          color: 'green',
          strokeType: 'dashed',
          strokeOpacity: 0.5,
        },
        {
          id: 'previous',
          name: this.$t('Previous month'),
          color: 'grey',
          strokeType: 'dashed',
          strokeDasharray: '8,4,2,4',
          strokeOpacity: 0.5,
          strokeWidth: 2,
        },
      ],

      monthOnMonthSeries: [
        {
          id: 'previous_cost',
          name: this.$t('Previous month'),
        },
        {
          id: 'cost',
          name: this.$t('Cost'),
        },
        {
          id: 'forecast',
          name: this.$t('Forecast'),
        },
      ],

      // Progress bar
      progressBarSeries: [
        {
          name: 'Amount',
          id: 'amount',
          fill: am4core.color(chartColors.green),
          fillOpacity: 0.9,
        },
        {
          name: 'Remaining',
          id: 'remaining',
          fill: am4core.color(chartColors.grey),
          fillOpacity: 0.4,
        },
      ],
    };
  },
  mounted() {
    this.generateChart();
  },
  computed: {
    ...mapGetters('costForecast', [
      'getChartDataMonthlyAll',
      'getChartDataMonthlyLatestUpdateTime',
      'getChartDataMonthToDate',
      'getChartDataMonthToDateBudget',
      'getChartDataMonthToDateLatestUpdateTime',
      'getChartDataMonthOnMonth',
      'getChartDataMonthOnMonthLatestUpdateTime',
      // Summary - Monthly
      'getSummaryMonthlyTotal',
      'getSummaryMonthlyRemaining',
      'getSummaryMonthlyForecastTotal',
      'getSummaryMonthlyBudgetRemaining',
      'getSummaryMonthlyBudgetProgress',
      // Summary - Daily
      'getSummaryMonthToDateTotal',
      'getSummaryMonthToDateForecastTotal',
      'getSummaryMonthToDateForecastRemaining',
      'getSummaryMonthToDateBudgetRemaining',
      'getSummaryMonthToDateBudgetProgress',
    ]),

    ...mapGetters('budget', ['getBudget', 'getBudgetTerm', 'getBudgetTotal']),

    monthlySeries() {
      const optional = [];

      if (this.getBudgetTotal) {
        optional.push({
          id: 'budget',
          name: this.$t('Budget'),
        });
      }

      const requirement = [
        {
          id: 'cost',
          name: this.$t('Cost'),
          color: 'indigo',
          strokeWidth: 2,
        },
        {
          id: 'forecast',
          name: this.$t('Forecast'),
          color: 'blue',
          strokeType: 'dashed',
          strokeWidth: 2,
        },
        {
          id: 'upperBound',
          name: this.$t('Upper bound'),
          color: 'red',
          strokeType: 'dashed',
          strokeOpacity: 0.5,
          strokeWidth: 2,
        },
        {
          id: 'lowerBound',
          name: this.$t('Lower bound'),
          color: 'green',
          strokeType: 'dashed',
          strokeOpacity: 0.5,
          strokeWidth: 2,
        },
      ];

      return [...optional, ...requirement];
    },

    budgetTerm() {
      return this.getBudgetTerm;
    },

    chartMonthlyData() {
      return this.getChartDataMonthlyAll;
    },

    chartMonthlyLatestUpdateTime() {
      return this.getChartDataMonthlyLatestUpdateTime;
    },

    chartMonthToDateData() {
      return this.getChartDataMonthToDate;
    },

    chartMonthToDateBudget() {
      return this.getChartDataMonthToDateBudget;
    },

    chartMonthToDateLatestUpdateTime() {
      return this.getChartDataMonthToDateLatestUpdateTime;
    },

    chartMonthOnMonthData() {
      return this.getChartDataMonthOnMonth;
    },

    chartMonthOnMonthLatestUpdateTime() {
      return this.getChartDataMonthOnMonthLatestUpdateTime;
    },

    summaryMonthly() {
      return {
        total: this.getSummaryMonthlyTotal,
        forecast: this.getSummaryMonthlyForecastTotal,
        remaining: this.getSummaryMonthlyRemaining,
        budgetTotal: this.getBudgetTotal,
        budgetRemaining: this.getSummaryMonthlyBudgetRemaining,
      };
    },

    progressMonthly() {
      const budgetProgress = this.getSummaryMonthlyBudgetProgress;

      return [
        {
          category: '',
          amount: budgetProgress.used,
          remaining: budgetProgress.remaining,
        },
      ];
    },

    summaryDaily() {
      return {
        total: this.getSummaryMonthToDateTotal,
        forecast: this.getSummaryMonthToDateForecastTotal,
        remaining: this.getSummaryMonthToDateForecastRemaining,
        budgetTotal: this.getChartDataMonthToDateBudget,
        budgetRemaining: this.getSummaryMonthToDateBudgetRemaining,
      };
    },

    progressMonthToDate() {
      const budgetProgress = this.getSummaryMonthToDateBudgetProgress;

      return [
        {
          category: '',
          amount: budgetProgress.used,
          remaining: budgetProgress.remaining,
        },
      ];
    },

    showBudgetBarMonthly() {
      return this.getBudgetTotal !== 0;
    },

    showBudgetBarMonthToDate() {
      return this.getChartDataMonthToDateBudget !== 0;
    },
  },
  methods: {
    ...mapActions('costForecast', [
      'fetchForecastMonthly',
      'fetchForecastMonthToDate',
      'fetchForecastMonthOnMonth',
    ]),
    ...mapActions('budget', ['fetchBudget']),

    ...mapMutations('costForecast', ['updateFetchInitial']),

    generateChart() {
      this.$refs['forecast-monthly-chart'].generateChart();
      this.$refs['forecast-mtd-chart'].generateChart();
      this.$refs['forecast-mom-chart'].generateChart();
      this.$refs['cost-forecast-monthly-budget-progress'].generateChart();
      this.$refs['cost-forecast-mtd-budget-progress'].generateChart();

      // return Promise.resolve('forecast');
    },

    setChartData() {
      this.$refs['forecast-monthly-chart'].setData();
      this.$refs['forecast-mtd-chart'].setData();
      this.$refs['forecast-mom-chart'].setData();
      this.$refs['cost-forecast-monthly-budget-progress'].setData();
      this.$refs['cost-forecast-mtd-budget-progress'].setData();
    },
  },
});
</script>

<style lang="scss" scoped>
.__CostForecast {
  &-Summary {
    margin: 0 $space-md $space-md;
    padding: 0 $space-md;

    & > div {
      padding-top: $space-sm;
    }
  }

  &-SummaryItemValue {
    font-size: $font-size-xl;
    height: 38px;

    &.bold {
      font-weight: 500;
    }

    &.center {
      padding-top: 12px;
    }

    &.small {
      font-size: $font-size-lg;
    }
  }

  @media (min-width: $lg) {
    &-SummaryItemValue {
      font-size: $font-size-xxl;
      font-weight: 300;
    }
  }

  &-SummaryItem {
  }

  &-ProgressBar {
    display: flex;
    align-items: baseline;
  }

  &-ProgressBarHeadLabel {
    font-size: $font-size-sm;
    margin-right: $space-xxs;
  }

  &-ProgressBarHeadSeparate {
    padding: 0 $space-sm;
  }

  &-ProgressBarFootContent {
    font-size: $font-size-sm;
    text-align: right;
    padding-right: 54px;
  }

  &-BudgetInfo {
    width: 200px;
    height: 273px;
    padding-right: $space-sm;
    border-right: 1px solid $border-color;
  }
}
</style>
